/** `rule.checkpoint` — an entrance/checkpoint marker. On the tick after it
 * appears (placed by a level or a phase layout) it sends every player a
 * `checkpoint` signal at its position; core.vitals moves the respawn point
 * there. With `warp` it also parks every player so vitals respawns them
 * there on the next tick without a life lost (a new level's entrance). With `touch`, a player touching it later re-arms it there. */
import { bool, id, obj } from '../schemaBuilders.js';
import { defineModule } from '../module.js';
import { overlaps } from '../world.js';

export type CheckpointConfig = { playerTag: string; warp: boolean; touch: boolean };

export const checkpoint = defineModule<CheckpointConfig>({
  id: 'rule.checkpoint', version: 1, attach: 'entity', role: 'rule',
  summary: 'Entrance/checkpoint: when it appears it sets every player’s respawn point (optionally warping them there); touching it re-sets it.',
  config: obj({ playerTag: id(), warp: bool(), touch: bool() }),
  requires: ['core.vitals'], events: ['checkpoint'], presentation: { checkpoint: { cue: 'none' } },
  proofs: ['appearing-sets-respawn', 'warp-moves-players', 'touch-rearms'],
  animStates: () => [],
  init(ctx, e) { ctx.data(e).sent = false; },
  systems: {
    // Think: vitals consumes the signal in this tick's rules stage.
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      const players = ctx.entities({ tag: c.playerTag, status: 'any' });
      const targets = d.sent !== true ? players : c.touch ? players.filter(p => p.status === 'active' && overlaps(ctx.world, p, e)) : [];
      if (!targets.length) return;
      const warp = d.sent !== true && c.warp;
      for (const p of targets) {
        ctx.signal('checkpoint', p, e, { x: e.x, y: e.y });
        if (warp) ctx.signal('park', p, e, { ticks: 2 }); // hidden this tick, back the next
      }
      if (d.sent !== true) ctx.emit('checkpoint', { x: e.x, y: e.y });
      d.sent = true;
    },
  },
});
